// AdminPlans — master operator view of SaaS pricing.
//
// Each row is a chitos_platform.plans entry. Admin can create new plans,
// edit metadata (label, description, features, visibility), archive, and
// "save as new version" for any price change once a plan has subscribers.

import { useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';

type Tier = 'starter' | 'growth' | 'scale' | 'enterprise';

interface PlanRow {
  id: string;
  tier: Tier;
  label: string;
  description: string;
  features: string[];
  monthly_price_paise: number;
  yearly_price_paise: number;
  max_active_groups: number | null;
  razorpay_plan_id_monthly: string | null;
  razorpay_plan_id_yearly: string | null;
  is_public: boolean;
  is_archived: boolean;
  created_at: string | Date;
  updated_at: string | Date;
}

function format_inr(paise: number): string {
  return (paise / 100).toLocaleString('en-IN');
}

export function AdminPlans(): ReactElement {
  const qc = useQueryClient();
  const [show_archived, set_show_archived] = useState(false);
  const [editing, set_editing] = useState<PlanRow | null>(null);
  const [creating, set_creating] = useState(false);

  const q = useQuery({
    queryKey: ['admin-plans', show_archived],
    queryFn: () => orpc.admin.list_plans_admin({ include_archived: show_archived }),
  });

  const archive_m = useMutation({
    mutationFn: (id: string) => orpc.admin.archive_plan({ id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-plans'] }),
  });

  const plans = (q.data?.plans ?? []) as PlanRow[];

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-medium">Plans</h1>
          <p className="text-xs text-ink/60 mt-1">
            SaaS pricing tiers. Each plan auto-creates a monthly + yearly Razorpay plan; subscribers bill against those.
            Editing prices on a plan with subscribers requires "Save as new version".
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-ink/60 flex items-center gap-1">
            <input
              type="checkbox"
              checked={show_archived}
              onChange={(e) => set_show_archived(e.target.checked)}
            />{' '}
            Show archived
          </label>
          <button
            type="button"
            onClick={() => set_creating(true)}
            className="bg-primary hover:bg-primary-2 text-white text-sm px-4 py-2 rounded font-medium"
          >
            + New plan
          </button>
        </div>
      </div>

      {q.isLoading && <div className="text-sm text-ink/60">Loading…</div>}
      {q.error && (
        <div className="text-sm text-bad px-3 py-2 bg-bad-soft rounded">
          {friendly_error(q.error)}
        </div>
      )}

      {!q.isLoading && plans.length === 0 && (
        <div className="bg-surface border border-line rounded-lg p-8 text-center text-sm text-ink/60">
          No plans yet. Create one to enable signup.
        </div>
      )}

      {plans.length > 0 && (
        <div className="bg-surface border border-line rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-bg-elev text-ink/60 text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Plan</th>
                <th className="text-left px-4 py-2 font-medium">Monthly</th>
                <th className="text-left px-4 py-2 font-medium">Yearly</th>
                <th className="text-left px-4 py-2 font-medium">Razorpay ids</th>
                <th className="text-left px-4 py-2 font-medium">Visibility</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id} className="border-t border-line/40">
                  <td className="px-4 py-3">
                    <div className="font-medium">{p.label}</div>
                    <div className="text-[11px] font-mono text-ink/50">{p.tier} · {p.id.slice(0, 8)}</div>
                    {p.description && (
                      <div className="text-xs text-ink/60 mt-1">{p.description}</div>
                    )}
                    {p.features.length > 0 && (
                      <ul className="text-[11px] text-ink/60 mt-1">
                        {p.features.map((f, i) => (
                          <li key={i}>• {f}</li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink/80">
                    ₹{format_inr(p.monthly_price_paise)}
                  </td>
                  <td className="px-4 py-3 text-ink/80">
                    ₹{format_inr(p.yearly_price_paise)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-[10px] font-mono text-ink/50 break-all">
                      M: {p.razorpay_plan_id_monthly ?? '—'}
                    </div>
                    <div className="text-[10px] font-mono text-ink/50 break-all">
                      Y: {p.razorpay_plan_id_yearly ?? '—'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {p.is_archived ? (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-bg-elev text-ink/50">
                        archived
                      </span>
                    ) : p.is_public ? (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-good-soft text-good">
                        public
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-warn-soft text-warn">
                        private
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => set_editing(p)}
                        disabled={p.is_archived}
                        className="text-xs px-2 py-1 border border-line text-ink/70 rounded hover:bg-bg-elev disabled:opacity-40"
                      >
                        Edit
                      </button>
                      {!p.is_archived && (
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Archive ${p.label}? Existing subscribers keep paying the old rate.`)) {
                              archive_m.mutate(p.id);
                            }
                          }}
                          disabled={archive_m.isPending}
                          className="text-xs px-2 py-1 border border-bad/30 text-bad rounded hover:bg-bad-soft disabled:opacity-50"
                        >
                          Archive
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <PlanModal
          plan={editing}
          on_close={() => {
            set_creating(false);
            set_editing(null);
          }}
          on_saved={() => {
            set_creating(false);
            set_editing(null);
            void qc.invalidateQueries({ queryKey: ['admin-plans'] });
          }}
        />
      )}
    </div>
  );
}

// ─── Modal ─────────────────────────────────────────────────────────────────

function PlanModal({
  plan,
  on_close,
  on_saved,
}: {
  plan: PlanRow | null;
  on_close: () => void;
  on_saved: () => void;
}): ReactElement {
  const editing = plan !== null;
  const [tier, set_tier] = useState<Tier>(plan?.tier ?? 'starter');
  const [label, set_label] = useState(plan?.label ?? '');
  const [description, set_description] = useState(plan?.description ?? '');
  const [features, set_features] = useState((plan?.features ?? []).join('\n'));
  const [monthly_rupees, set_monthly_rupees] = useState(
    plan ? String(plan.monthly_price_paise / 100) : '',
  );
  const [yearly_rupees, set_yearly_rupees] = useState(
    plan ? String(plan.yearly_price_paise / 100) : '',
  );
  const [max_groups, set_max_groups] = useState(
    plan?.max_active_groups != null ? String(plan.max_active_groups) : '',
  );
  const [is_public, set_is_public] = useState(plan?.is_public ?? true);

  const create_m = useMutation({
    mutationFn: () =>
      orpc.admin.create_plan({
        tier,
        label,
        description,
        features: features.split('\n').map((s) => s.trim()).filter(Boolean),
        monthly_price_paise: Math.round(Number(monthly_rupees) * 100),
        yearly_price_paise: Math.round(Number(yearly_rupees) * 100),
        max_active_groups: max_groups ? Number(max_groups) : null,
        overage_paise_per_group: 0,
        is_public,
      }),
    onSuccess: on_saved,
  });

  const meta_m = useMutation({
    mutationFn: () =>
      orpc.admin.update_plan_metadata({
        id: plan!.id,
        label,
        description,
        features: features.split('\n').map((s) => s.trim()).filter(Boolean),
        is_public,
        max_active_groups: max_groups ? Number(max_groups) : null,
      }),
    onSuccess: on_saved,
  });

  const new_version_m = useMutation({
    mutationFn: () =>
      orpc.admin.save_as_new_version({
        previous_id: plan!.id,
        label,
        description,
        features: features.split('\n').map((s) => s.trim()).filter(Boolean),
        monthly_price_paise: Math.round(Number(monthly_rupees) * 100),
        yearly_price_paise: Math.round(Number(yearly_rupees) * 100),
        max_active_groups: max_groups ? Number(max_groups) : null,
        overage_paise_per_group: 0,
        is_public,
      }),
    onSuccess: on_saved,
  });

  const monthly_changed =
    editing && Math.round(Number(monthly_rupees) * 100) !== plan!.monthly_price_paise;
  const yearly_changed =
    editing && Math.round(Number(yearly_rupees) * 100) !== plan!.yearly_price_paise;
  const price_changed = monthly_changed || yearly_changed;

  const error =
    create_m.error || meta_m.error || new_version_m.error;
  const pending = create_m.isPending || meta_m.isPending || new_version_m.isPending;

  const handle_submit = (e: FormEvent): void => {
    e.preventDefault();
    if (!editing) {
      create_m.mutate();
      return;
    }
    if (price_changed) {
      if (
        !window.confirm(
          `Price change requires a new plan version. The old plan ('${plan!.label}') will be archived; existing subscribers keep paying its current rate. Continue?`,
        )
      )
        return;
      new_version_m.mutate();
    } else {
      meta_m.mutate();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !pending) on_close();
      }}
    >
      <div className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-medium">{editing ? 'Edit plan' : 'New plan'}</h2>
          <button
            type="button"
            onClick={on_close}
            disabled={pending}
            className="text-ink/50 hover:text-ink text-lg leading-none p-1"
          >
            ×
          </button>
        </div>

        <form onSubmit={handle_submit} className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block mb-1">Tier</span>
              <select
                value={tier}
                onChange={(e) => set_tier(e.target.value as Tier)}
                disabled={editing}
                className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
              >
                <option value="starter">starter</option>
                <option value="growth">growth</option>
                <option value="scale">scale</option>
                <option value="enterprise">enterprise</option>
              </select>
            </label>

            <label className="text-sm">
              <span className="block mb-1">Label</span>
              <input
                type="text"
                value={label}
                onChange={(e) => set_label(e.target.value)}
                required
                minLength={1}
                maxLength={120}
                className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </label>
          </div>

          <label className="text-sm">
            <span className="block mb-1">Description</span>
            <input
              type="text"
              value={description}
              onChange={(e) => set_description(e.target.value)}
              maxLength={500}
              className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </label>

          <label className="text-sm">
            <span className="block mb-1">Features (one per line)</span>
            <textarea
              value={features}
              onChange={(e) => set_features(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block mb-1">Monthly price (₹)</span>
              <input
                type="number"
                value={monthly_rupees}
                onChange={(e) => set_monthly_rupees(e.target.value)}
                min={0}
                step={1}
                required
                className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1">Yearly price (₹)</span>
              <input
                type="number"
                value={yearly_rupees}
                onChange={(e) => set_yearly_rupees(e.target.value)}
                min={0}
                step={1}
                required
                className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block mb-1">Max active groups (blank = unlimited)</span>
              <input
                type="number"
                value={max_groups}
                onChange={(e) => set_max_groups(e.target.value)}
                min={0}
                step={1}
                className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </label>
            <label className="text-sm flex items-end pb-2">
              <input
                type="checkbox"
                checked={is_public}
                onChange={(e) => set_is_public(e.target.checked)}
                className="mr-2"
              />
              <span>Visible on public signup</span>
            </label>
          </div>

          {editing && price_changed && (
            <div className="px-3 py-2 bg-warn-soft text-warn text-xs rounded">
              You changed a price. Saving will archive the current plan and create a new
              version. Existing tenants keep paying the old rate; new signups use the
              new prices.
            </div>
          )}

          {error && (
            <div className="px-3 py-2 bg-bad-soft text-bad text-xs rounded">
              {friendly_error(error)}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={on_close}
              disabled={pending}
              className="text-sm px-3 py-2 text-ink/60 hover:text-ink rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={pending}
              className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white text-sm px-4 py-2 rounded font-medium"
            >
              {pending
                ? 'Saving…'
                : !editing
                ? 'Create plan'
                : price_changed
                ? 'Save as new version'
                : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
